import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Link, useNavigate } from 'react-router-dom';
import LoadingAnimation from '../components/loadingAnimation';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../styles/RuntimeLogs.css';
import { fetchLogsInventory, fetchLogsTail } from '../utils/logsApi';
import { NetworkCellRenderer, EntityCellRenderer } from '../utils/metrics';
import { getApiBase } from '../utils/apiBase';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';

function SeverityBadge({ level }) {
  const text = level || 'info';
  const color = text === 'error' ? '#ef4444' : text === 'warn' ? '#f59e0b' : '#6b7280';
  return (
    <span style={{
      display: 'inline-block',
      borderRadius: '6px',
      padding: '2px 6px',
      fontSize: '11px',
      background: color,
      color: 'white'
    }}>{text.toUpperCase()}</span>
  );
}

function parseSeverity(line) {
  try {
    const raw = line?.raw || '';
    // try JSON first
    const idx = raw.indexOf('{');
    if (idx >= 0) {
      const maybe = raw.slice(idx);
      try {
        const obj = JSON.parse(maybe);
        const lvl = (obj.level || obj.severity || '').toString().toLowerCase();
        if (lvl) return lvl.includes('error') ? 'error' : lvl.includes('warn') ? 'warn' : 'info';
      } catch {}
    }
    const s = raw.toLowerCase();
    if (/(\berror\b|\berr\b|\bexception\b)/.test(s)) return 'error';
    if (/(\bwarn\b|\bwarning\b)/.test(s)) return 'warn';
    return 'info';
  } catch { return 'info'; }
}

function parseSeverityForErrors(line) {
  try {
    const raw = line?.raw || '';
    
    // Special case: treat non-fatal errors as warnings
    if (/non[-\s]?fatal\s*error/i.test(raw)) return 'warn';
    
    // try JSON first
    const idx = raw.indexOf('{');
    if (idx >= 0) {
      const maybe = raw.slice(idx);
      try {
        const obj = JSON.parse(maybe);
        const lvl = (obj.level || obj.severity || '').toString().toLowerCase();
        if (lvl) return lvl.includes('error') ? 'error' : lvl.includes('warn') ? 'warn' : 'info';
      } catch {}
    }
    
    const s = raw.toLowerCase();
    
    // Filter out false positives - messages that contain [ERR] but are actually INFO messages
    // These are log categories/modules, not actual errors
    if (/\[(submission_summary|fast_updates|results|indexer|external_chain_heights|fdc_submission_summary|fdc_submissions|feed_config|feed_counts|ftso_performance_dashboard|indexer_states|node_health|node_info|pivot_voting_round|submit2prices|validator_info|vote_power|voter_registration)\]\[err\]/i.test(s)) {
      return 'info'; // These are just INFO messages from specific modules
    }
    
    // Look for actual error indicators, but be more specific
    if (/(\berror\b|\bexception\b|\bfailed\b|\bcrash\b|\bpanic\b)/.test(s)) {
      // But exclude cases where "error" is part of a filename or module name
      if (/\.(log|json|txt|csv)\b/i.test(s) || /\w+_error\w*/i.test(s)) {
        return 'info';
      }
      return 'error';
    }
    
    if (/(\bwarn\b|\bwarning\b)/.test(s)) return 'warn';
    return 'info';
  } catch { return 'info'; }
}

export default function RuntimeLogs() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTarget, setActiveTarget] = useState(null); // { instanceId, container }
  const [logLines, setLogLines] = useState([]);
  const [filter, setFilter] = useState('all'); // all|errors|warnings
  const [search, setSearch] = useState('');
  const logsGridRef = useRef(null);
  const [logsGridApi, setLogsGridApi] = useState(null);
  const [currentView, setCurrentView] = useState('instances'); // instances|errors
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLoading, setErrorLoading] = useState(false);
  const { getCachedData, setCachedData } = useDataCache();
  const errorGridApiRef = useRef(null);
  const errorEventSrcRef = useRef(null);
  const latestUpdate = useMemo(() => {
    try {
      const maxTs = rows.reduce((m, r) => Math.max(m, r.lastUpdated || 0), 0);
      if (!maxTs) return '';
      const d = new Date(maxTs);
      return d.toLocaleString('en-GB', {
        year: '2-digit', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
      }).replace(',', '');
    } catch { return ''; }
  }, [rows]);

  const formatTs = useCallback((ts) => {
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts || '';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const mi = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      const ms = String(d.getMilliseconds()).padStart(3, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}.${ms}`;
    } catch { return ts || ''; }
  }, []);

  const parseLineToRow = useCallback((l) => {
    const level = parseSeverity(l);
    const raw = String(l?.raw || '');
    const sourceMatch = raw.match(/([A-Za-z0-9_\/\.\-]+\.go:\d+)/);
    const source = sourceMatch ? sourceMatch[1] : '';
    let message = raw;
    if (sourceMatch) {
      const idx = raw.indexOf(source);
      if (idx >= 0) message = raw.slice(idx + source.length).trim();
    }
    return {
      seq: l.seq,
      time: formatTs(l.ts),
      level,
      source,
      message,
    };
  }, [formatTs]);

  const navigate = useNavigate();
  const openLogs = useCallback((row) => {
    navigate(`/runtime-logs/${encodeURIComponent(row.instanceId)}/${encodeURIComponent(row.container)}`);
  }, [navigate]);

  const fetchAllErrorLogs = useCallback(async () => {
    if (currentView !== 'errors') return;
    
    setErrorLoading(true);
    try {
      const inv = await fetchLogsInventory();
      const allErrors = [];
      
      // Get all containers that have logs available
      const containersWithLogs = [];
      (inv.instances || []).forEach(inst => {
        (inst.containers || []).forEach(c => {
          if (c.hasLogs) {
            containersWithLogs.push({
              instanceId: inst.instanceId,
              container: c.name,
              chain: inst.chain,
              entity: inst.entity,
              host: inst.host
            });
          }
        });
      });

      // Fetch logs from each container and filter for errors
      for (const container of containersWithLogs) {
        try {
          const logsData = await fetchLogsTail({
            instanceId: container.instanceId,
            container: container.container,
            limit: 1000 // Get recent logs to scan for errors
          });
          
          // Filter for error logs and add metadata
          (logsData.lines || []).forEach(line => {
            const level = parseSeverityForErrors(line);
            if (level === 'error') {
              allErrors.push({
                ...line,
                chain: container.chain,
                entity: container.entity,
                instanceId: container.instanceId,
                container: container.container,
                host: container.host,
                level: level
              });
            }
          });
        } catch (err) {
          console.warn(`Failed to fetch logs for ${container.instanceId}/${container.container}:`, err);
        }
      }
      
      // Sort by timestamp (oldest first, newest last)
      allErrors.sort((a, b) => new Date(a.ts) - new Date(b.ts));
      
      setErrorLogs(allErrors);
    } catch (err) {
      console.error('Failed to fetch error logs:', err);
      setErrorLogs([]);
    } finally {
      setErrorLoading(false);
    }
  }, [currentView]);

  const UpDownCellRenderer = (params) => {
    const raw = (params.value ?? '').toString().trim().toLowerCase();
    const v = raw === 'up' ? 'UP' : raw === 'down' ? 'DOWN' : (raw || 'N/A').toUpperCase();
    let statusClass = 'status-pending';
    let tooltipText = '';
    
    if (raw === 'up') {
      statusClass = 'status-success';
      tooltipText = 'Docker container running';
    } else if (raw === 'down') {
      // Use blue pill if expectedDown is true on the row
      const isExpected = params?.data?.expectedDown === true;
      statusClass = isExpected ? 'status-info' : 'status-missing';
      tooltipText = isExpected ? 'Docker container not running (expected)' : 'Docker container not running';
    }
    
    return (
      <div className="status-cell-wrapper">
        <div 
          className={`status-cell ${statusClass} runtime-logs-status`}
          title={tooltipText}
          style={{
            cursor: 'default'
          }}
        >
          {v || 'N/A'}
        </div>
      </div>
    );
  };

  const LogsAvailableCellRenderer = (params) => {
    const hasLogs = params?.data?.hasLogs === true;
    return (
      <div className="status-cell-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div 
          className="log-availability-dot"
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: hasLogs ? '#10b981' : '#ef4444',
            display: 'inline-block'
          }}
          title={hasLogs ? 'Logs available' : 'No logs available'}
        />
      </div>
    );
  };

  const DiskSpaceCellRenderer = ({ data }) => {
    if (!data.diskSpace) return <span style={{ color: '#888' }}>-</span>;
    
    const { size, available, used, usePercent } = data.diskSpace;
    const usageNum = parseInt(usePercent?.replace('%', '') || '0', 10);
    
    // Color based on usage percentage
    let color = '#4CAF50'; // Green for low usage
    if (usageNum > 80) color = '#f44336'; // Red for high usage
    else if (usageNum > 60) color = '#FF9800'; // Orange for medium usage
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ 
          width: '50px', 
          height: '6px', 
          backgroundColor: '#333', 
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${usageNum}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '12px', color: '#ccc', minWidth: '80px' }}>
          {used} / {size}
        </span>
      </div>
    );
  };

  const columnDefs = useMemo(() => ([
    { field: 'entity', headerName: 'Entity', minWidth: 100, maxWidth: 120, flex: 0.9, cellRenderer: EntityCellRenderer },
    { field: 'chain', headerName: 'Chain', minWidth: 140, maxWidth: 140, flex: 0.9, cellRenderer: NetworkCellRenderer },
    { field: 'instanceId', headerName: 'Instance', minWidth: 180, flex: 1.1 },
    { field: 'container', headerName: 'Container', minWidth: 420, flex: 1.2 },
    { field: 'host', headerName: 'Host', minWidth: 140, flex: 1 },
    { headerName: 'Disk Space', minWidth: 170, maxWidth: 170, flex: 0.9, cellRenderer: DiskSpaceCellRenderer, filter: false, sortable: false },
    { field: 'status', headerName: 'Status', minWidth: 120, maxWidth: 140, flex: 0.7, cellRenderer: UpDownCellRenderer },
    { field: 'lastUpdated', headerName: 'Last Update', minWidth: 180, flex: 1.1,
      valueFormatter: p => p.value ? new Date(p.value).toLocaleString() : '' },
    { field: 'hasLogs', headerName: 'Logs Available', minWidth: 100, maxWidth: 100, flex: 0.7, cellRenderer: LogsAvailableCellRenderer, filter: false, sortable: true },
    { headerName: 'Logs', minWidth: 100, maxWidth: 100, filter: false, sortable: false,
      cellRenderer: (p) => (
        <button onClick={() => openLogs(p.data)} className="btn-logs text-button">View</button>
      )
    },
  ]), [openLogs]);

  const defaultColDef = useMemo(() => ({ resizable: true, sortable: true, filter: true }), []);

  // Column definitions for errors grid
  const errorColumnDefs = useMemo(() => ([
    { field: 'time', headerName: 'Time', minWidth: 180, maxWidth: 200, filter: 'agDateColumnFilter',
      valueGetter: (params) => new Date(params.data.ts),
      valueFormatter: (params) => params.value ? formatTs(params.data.ts) : '' },
    { field: 'entity', headerName: 'Entity', minWidth: 80, maxWidth: 100, cellRenderer: EntityCellRenderer },
    { field: 'chain', headerName: 'Chain', minWidth: 100, maxWidth: 120, cellRenderer: NetworkCellRenderer },
    { field: 'instanceId', headerName: 'Instance', minWidth: 160, flex: 0.8 },
    { field: 'container', headerName: 'Container', minWidth: 210, flex: 0.8 },
    { field: 'level', headerName: 'Level', minWidth: 80, maxWidth: 100, 
      cellRenderer: (params) => (
        <div className="status-cell-wrapper">
          <div className="status-cell status-missing">ERROR</div>
        </div>
      )
    },
    { field: 'raw', headerName: 'Message', minWidth: 800, flex: 2, 
      wrapText: true, autoHeight: true, filter: 'agTextColumnFilter',
      valueGetter: (params) => {
        // Filter out empty messages or timestamp-only messages like in RuntimeLogDetail
        const raw = String(params.data.raw || '');
        const trimmedMessage = raw.trim();
        if (!trimmedMessage) return '';
        
        // Check if message is just a timestamp
        const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
        if (timestampPattern.test(trimmedMessage)) return '';
        
        // Remove timestamp from beginning of message if it exists
        const timestampPrefixPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?\s*/;
        const bracketTimestampPattern = /^\[\d{2}-\d{2}\|\d{2}:\d{2}:\d{2}\.\d{3}\]\s*/;
        // Remove NEST format: [Nest] 1  - 08/26/2025, 10:36:48 AM (note the extra spaces)
        const nestTimestampPattern = /^\[Nest\]\s*\d+\s*-\s*\d{2}\/\d{2}\/\d{4},\s*\d{1,2}:\d{2}:\d{2}\s*(AM|PM)\s*/;
        // Remove [0:33MERROR format timestamps
        const goTimestampPattern = /^\[\d+:\d+[A-Z]+ERROR\s*/;
        
        let cleanedMessage = trimmedMessage
          .replace(timestampPrefixPattern, '')
          .replace(bracketTimestampPattern, '')
          .replace(nestTimestampPattern, '')
          .replace(goTimestampPattern, '');
        
        // Remove "FETCHING" word from the beginning of messages
        cleanedMessage = cleanedMessage.replace(/^FETCHING\s+/i, '');
        
        // Parse structured logs: INFO | SOURCE | CONTENT
        // Handle both formats: "INFO SOURCE CONTENT" and "WARN [SOURCE] CONTENT"
        const structuredLogPattern1 = /^(INFO|WARN|WARNING|ERROR|DEBUG|LOG)\s+([A-Z_/]+(?:\.[A-Z_]+)*(?:\.GO:\d+)?)\s+(.+)$/i;
        const structuredLogPattern2 = /^(INFO|WARN|WARNING|ERROR|DEBUG|LOG)\s+\[([A-Z_/]+[A-Z_/]*)\]\s+(.+)$/i;
        
        let structuredMatch = cleanedMessage.match(structuredLogPattern1) || cleanedMessage.match(structuredLogPattern2);
        
        if (structuredMatch) {
          const [, logLevel, source, content] = structuredMatch;
          // Format as: SOURCE | CONTENT (level is already shown in separate column)
          cleanedMessage = `${source}|${content}`;
        }
        
        return cleanedMessage;
      },
      cellRenderer: (params) => {
        const message = params.value;
        if (!message) return '';
        
        // Truncate very long messages
        const maxChars = 500;
        const truncated = message.length > maxChars ? `${message.slice(0, maxChars)}...` : message;
        
        // Check if message has the structured format with separators (SOURCE | CONTENT)
        const separatorPattern = /^([A-Z_/\.:\d]+)\|(.+)$/i;
        const match = truncated.match(separatorPattern);
        
        if (match) {
          const [, source, content] = match;
          return (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'flex', flexWrap: 'nowrap' }}>
              <span style={{ color: '#7dd3fc', flexShrink: 0, marginRight: '4px' }}>{source}</span>
              <span style={{ color: '#ffffff', flexShrink: 0, marginRight: '4px' }}> | </span>
              <span style={{ color: '#ffffff', flexGrow: 1 }}>{content}</span>
            </div>
          );
        }
        
        // Fallback for non-structured messages
        return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#ffffff' }}>{truncated}</div>;
      }
    }
  ]), [formatTs]);

  // Processed error rows
  const errorRows = useMemo(() => {
    return errorLogs
      .filter(log => {
        const message = log.raw || '';
        const trimmedMessage = message.trim();
        if (!trimmedMessage) return false;
        
        // Filter out timestamp-only messages
        const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
        return !timestampPattern.test(trimmedMessage);
      });
    // No need to reverse - already sorted with oldest first, newest last
  }, [errorLogs]);

  useEffect(() => {
    // Register for background prewarm
    try { cacheManager.registerEndpoints('runtime-logs', ['/api/logs/inventory']); } catch {}

    // Hydrate from cache immediately for instant paint
    try {
      const cached = getCachedData('/api/logs/inventory');
      if (cached && typeof cached === 'object') {
        const flat = [];
        (cached.instances || []).forEach(inst => {
          (inst.containers || []).forEach(c => {
            flat.push({
              instanceId: inst.instanceId,
              container: c.name,
              status: c.status,
              lastUpdated: c.lastUpdated,
              chain: inst.chain,
              entity: inst.entity,
              host: inst.host,
              expectedDown: !!c.expectedDown,
              hasLogs: !!c.hasLogs,
              diskSpace: inst.diskSpace || null
            });
          });
        });
        setRows(flat);
        setLoading(false);
      }
    } catch {}

    const load = async () => {
      setError(null);
      try {
        const inv = await fetchLogsInventory();
        setCachedData('/api/logs/inventory', inv);
        const flat = [];
        (inv.instances || []).forEach(inst => {
          (inst.containers || []).forEach(c => {
            flat.push({ 
              instanceId: inst.instanceId, 
              container: c.name, 
              status: c.status, 
              lastUpdated: c.lastUpdated, 
              chain: inst.chain, 
              entity: inst.entity, 
              host: inst.host, 
              expectedDown: !!c.expectedDown, 
              hasLogs: !!c.hasLogs,
              diskSpace: inst.diskSpace || null
            });
          });
        });
        setRows(flat);
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();

    return () => {
      try { cacheManager.unregisterEndpoints('runtime-logs'); } catch {}
    };
  }, []);

  // Fetch error logs when switching to errors tab
  useEffect(() => {
    if (currentView === 'errors') {
      // Clear existing error logs to force fresh data with updated formatting
      setErrorLogs([]);
      fetchAllErrorLogs();
    }
  }, [currentView, fetchAllErrorLogs]);

  // Scroll to bottom when new errors arrive (real-time updates)
  useEffect(() => {
    if (currentView === 'errors' && errorGridApiRef.current && errorRows.length > 0) {
      // Only scroll for real-time updates, not initial load (handled by onGridReady)
      const isInitialLoad = errorLoading;
      if (!isInitialLoad) {
        setTimeout(() => {
          try {
            errorGridApiRef.current.ensureIndexVisible(errorRows.length - 1, 'bottom');
          } catch (err) {
            console.warn('Failed to scroll to bottom of errors grid:', err);
          }
        }, 100);
      }
    }
  }, [errorRows]); // Removed currentView dependency to avoid initial scroll conflicts

  // Real-time error updates via SSE when on errors tab
  useEffect(() => {
    if (currentView !== 'errors') return;

    let eventSource;

    const setupSSE = () => {
      try {
        const API_BASE = getApiBase();
        const es = new EventSource(`${API_BASE}/api/logs/events`);
        errorEventSrcRef.current = es;
        eventSource = es;

        es.addEventListener('logLine', (e) => {
          try {
            const payload = JSON.parse(e.data);
            // Check if this is an error log using our error classification
            const level = parseSeverityForErrors(payload);
            if (level === 'error') {
              // Filter out empty or timestamp-only messages
              const message = payload.raw || '';
              const trimmedMessage = message.trim();
              if (!trimmedMessage) return;
              
              const timestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z?$/;
              if (timestampPattern.test(trimmedMessage)) return;

              // Find the container metadata to add chain, entity, etc.
              const containerInfo = rows.find(r => 
                r.instanceId === payload.instanceId && 
                r.container === payload.container
              );

              if (containerInfo) {
                const newError = {
                  ...payload,
                  chain: containerInfo.chain,
                  entity: containerInfo.entity,
                  instanceId: payload.instanceId,
                  container: payload.container,
                  host: containerInfo.host,
                  level: level
                };

                // Add to existing error logs and sort by timestamp (keep last 2000 errors)
                setErrorLogs((prev) => {
                  const next = [...prev, newError];
                  // Sort by timestamp (oldest first, newest last)
                  next.sort((a, b) => (a.ts || 0) - (b.ts || 0));
                  // Keep last 2000 errors
                  return next.slice(-1999);
                });
              }
            }
          } catch (err) {
            console.warn('Failed to process SSE log line for errors:', err);
          }
        });

        es.onerror = (error) => {
          console.warn('SSE connection error for errors:', error);
          if (errorEventSrcRef.current) {
            errorEventSrcRef.current.close();
            errorEventSrcRef.current = null;
          }
        };
      } catch (err) {
        console.warn('Failed to setup SSE for errors:', err);
      }
    };

    setupSSE();

    return () => {
      if (errorEventSrcRef.current) {
        errorEventSrcRef.current.close();
        errorEventSrcRef.current = null;
      }
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [currentView, rows]); // Added rows dependency to get container metadata

  const parsedRows = useMemo(() => (logLines || []).map(parseLineToRow), [logLines, parseLineToRow]);

  const filteredLines = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (parsedRows || []).filter(r => {
      if (filter === 'errors' && r.level !== 'error') return false;
      if (filter === 'warnings' && r.level !== 'warn') return false;
      if (term && !(r.time + ' ' + r.level + ' ' + r.source + ' ' + r.message).toLowerCase().includes(term)) return false;
      return true;
    });
  }, [parsedRows, filter, search]);

  const logsColumnDefs = useMemo(() => ([
    { field: 'time', headerName: 'Time', minWidth: 200, maxWidth: 240, pinned: 'left', filter: 'agTextColumnFilter' },
    { field: 'level', headerName: 'Level', minWidth: 110, maxWidth: 130, cellRenderer: (p) => <SeverityBadge level={p.value} />, filter: 'agSetColumnFilter' },
    { field: 'source', headerName: 'Source', minWidth: 260, flex: 1, filter: 'agTextColumnFilter' },
    { field: 'message', headerName: 'Message', minWidth: 400, flex: 2, wrapText: true, autoHeight: true, filter: 'agTextColumnFilter' },
  ]), []);

  useEffect(() => {
    if (!logsGridApi) return;
    try {
      if (typeof logsGridApi.setQuickFilter === 'function') {
        logsGridApi.setQuickFilter(search);
      } else if (typeof logsGridApi.setGridOption === 'function') {
        logsGridApi.setGridOption('quickFilterText', search);
      }
    } catch {}
  }, [search, logsGridApi]);

  useEffect(() => {
    if (modalOpen && logsGridApi && filteredLines.length > 0) {
      logsGridApi.ensureIndexVisible(filteredLines.length - 1, 'bottom');
    }
  }, [modalOpen, filteredLines, logsGridApi]);

  if (loading) return <LoadingAnimation />;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="main-wrapper">
      {/* View Toggle */}
      <div className="series-toggles" style={{ width: '100%', padding: '0 15px 10px 0px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="network-toggle">
          <button
            className={currentView === 'instances' ? 'active' : ''}
            onClick={() => setCurrentView('instances')}
          >
            Instances
          </button>
          <button
            className={currentView === 'errors' ? 'active' : ''}
            onClick={() => setCurrentView('errors')}
          >
            Errors
          </button>
          <div className="slider"></div>
        </div>
      </div>

      {currentView === 'instances' && (
        <div className="grid-wrapper">
          <div className="ag-theme-alpine-dark custom-dark-theme">
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rows}
              defaultColDef={defaultColDef}
              rowHeight={40}
              headerHeight={45}
              pagination={true}
              paginationPageSize={200}
            />
          </div>
        </div>
      )}
      
      {currentView === 'errors' && (
        <div className="grid-wrapper">
          {errorLoading ? (
            <LoadingAnimation />
          ) : (
            <div className="ag-theme-alpine-dark custom-dark-theme">
              <AgGridReact
                key={`errors-grid-${errorRows.length}`}
                columnDefs={errorColumnDefs}
                rowData={errorRows}
                defaultColDef={defaultColDef}
                rowHeight={45}
                headerHeight={45}
                pagination={false}
                paginationPageSize={1000}
                paginationPageSizeSelector={[100, 500, 1000, 2000]}
                onGridReady={(params) => {
                  errorGridApiRef.current = params.api;
                  // Scroll to bottom immediately when grid is ready (like logs details)
                  if (errorRows.length > 0) {
                    params.api.ensureIndexVisible(errorRows.length - 1, 'bottom');
                  }
                }}
                getRowHeight={(params) => {
                  // Dynamic row height for long messages
                  const message = params.data.raw || '';
                  const lines = Math.ceil(message.length / 100); // Rough estimate
                  return Math.max(45, Math.min(lines * 20 + 25, 150)); // Min 45px, max 150px
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal removed; details moved to dedicated page */}
    </div>
  );
}


